/**
 * Registration payment summary derived from the append-only ledger.
 * This mirrors the SQL function `recompute_registration_payment` and both are
 * covered by tests so they cannot drift.
 */
export const REGISTRATION_STATUSES = ['interested', 'pending_confirmation', 'confirmed', 'waitlisted', 'cancelled', 'withdrawn'] as const;
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number];

/**
 * The statuses an admin picks (v1.41.0, owner): "รอคิว" is gone — a reserve team is the ทีมสำรอง tick now — and
 * "ยกเลิก" is gone too: a team that drops out is ถอนทีม, and that can be set back at any time. `cancelled` and
 * `waitlisted` stay in REGISTRATION_STATUSES so older registrations keep reading correctly.
 */
export const REGISTRATION_STATUS_CHOICES = ['interested', 'pending_confirmation', 'confirmed', 'withdrawn'] as const;

/**
 * "ทีมหลัก / ทีมสำรอง" of a registration (v1.36.0). A person ticks it: the tournament closes the main draw at 50
 * teams across all categories, later teams are the reserves of their own age group in registration order, and the
 * organiser may still admit teams above 50 before the draw — so nothing here is decided automatically.
 */
export const REGISTRATION_SQUADS = ['main', 'reserve'] as const;
export type RegistrationSquad = (typeof REGISTRATION_SQUADS)[number];

export const PAYMENT_STATUSES = ['unpaid', 'partially_paid', 'paid', 'overpaid', 'refunded'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_ENTRY_TYPES = ['payment', 'refund', 'adjustment', 'reversal'] as const;
export type PaymentEntryType = (typeof PAYMENT_ENTRY_TYPES)[number];

export const PAYMENT_METHODS = ['cash', 'transfer', 'promptpay', 'card', 'imported', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const VERIFICATION_STATUSES = ['pending_verification', 'verified', 'rejected'] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

export interface LedgerEntryLike {
  entryType: PaymentEntryType;
  amountMinor: number;
  verificationStatus: VerificationStatus;
}

export interface PaymentSummary {
  paidVerifiedMinor: number;
  pendingVerificationMinor: number;
  outstandingMinor: number;
  paymentStatus: PaymentStatus;
  depositMet: boolean;
}

export function paymentStatusFor(verifiedMinor: number, amountDueMinor: number, verifiedRefundCount: number): PaymentStatus {
  if (verifiedRefundCount > 0 && verifiedMinor <= 0) return 'refunded';
  if (amountDueMinor === 0 && verifiedMinor === 0) return 'paid';
  if (verifiedMinor <= 0) return 'unpaid';
  if (verifiedMinor < amountDueMinor) return 'partially_paid';
  if (verifiedMinor === amountDueMinor) return 'paid';
  return 'overpaid';
}

export function summarizeLedger(entries: readonly LedgerEntryLike[], amountDueMinor: number, depositRequiredMinor: number): PaymentSummary {
  let verified = 0;
  let pending = 0;
  let refunds = 0;
  for (const e of entries) {
    if (e.verificationStatus === 'verified') {
      verified += e.amountMinor;
      if (e.entryType === 'refund') refunds += 1;
    } else if (e.verificationStatus === 'pending_verification') {
      pending += e.amountMinor;
    }
  }
  return {
    paidVerifiedMinor: verified,
    pendingVerificationMinor: pending,
    outstandingMinor: Math.max(amountDueMinor - verified, 0),
    paymentStatus: paymentStatusFor(verified, amountDueMinor, refunds),
    // "มัดจำแล้ว" only when a deposit is required and the verified total reaches it.
    depositMet: depositRequiredMinor > 0 && verified >= depositRequiredMinor,
  };
}
